'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { Container, Title, Text, TextInput, Textarea, Button, Paper, Stack, Alert, Group, Box, Select } from '@mantine/core';
import { IconSend, IconCheck, IconAlertCircle, IconBug } from '@tabler/icons-react';
import { PageHero } from '@/components/PageHero';
import api from '@/lib/api';

const ISSUE_TYPES = [
  { value: 'wrong_part', label: 'Wrong part in bag' },
  { value: 'missing_part', label: 'Missing part' },
  { value: 'wrong_quantity', label: 'Wrong quantity' },
  { value: 'wrong_bag', label: 'Part in wrong bag' },
  { value: 'missing_set', label: 'Set not available' },
  { value: 'website_bug', label: 'Website bug/error' },
  { value: 'suggestion', label: 'Suggestion/feedback' },
  { value: 'other', label: 'Other' },
];

export default function ReportIssuePage() {
  return (
    <Suspense>
      <ReportIssuePageContent />
    </Suspense>
  );
}

function ReportIssuePageContent() {
  const searchParams = useSearchParams();
  const [setNumber, setSetNumber] = useState('');

  // Pre-fill set number from URL parameter
  useEffect(() => {
    const setParam = searchParams.get('set');
    if (setParam) {
      setSetNumber(setParam);
    }
  }, [searchParams]);
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!issueType) {
      setError('Please select an issue type');
      return;
    }

    if (!description.trim()) {
      setError('Please describe the issue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.reportIssue(
        setNumber.trim() || null,
        issueType,
        description.trim(),
        email.trim() || null
      );
      setSuccess(true);
      setSetNumber('');
      setIssueType('');
      setDescription('');
      setEmail('');
    } catch (err) {
      setError(err.message || 'Failed to submit issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewReport = () => {
    setSuccess(false);
    setError(null);
  };

  return (
    <>
      <PageHero
        icon={IconBug}
        iconColor="#e03131"
        iconBg="#fff0f0"
        title="Report an Issue"
        subtitle="Found a problem or have feedback? Let us know and we'll fix it!"
      />
      <Container size="sm" py="xl">
      <Stack gap="xl">
        {/* Success State */}
        {success ? (
          <Paper
            p="xl"
            radius="lg"
            style={{
              backgroundColor: '#d3f9d8',
              border: '1px solid #b2f2bb',
              textAlign: 'center',
            }}
          >
            <IconCheck size={48} color="#2f9e44" style={{ marginBottom: 16 }} />
            <Title order={3} mb="xs" style={{ color: '#2f9e44' }}>
              Issue Reported!
            </Title>
            <Text c="dimmed" mb="lg">
              Thank you for your feedback. We'll look into it as soon as possible.
            </Text>
            <Button
              variant="light"
              color="green"
              onClick={handleNewReport}
              radius="xl"
            >
              Report Another Issue
            </Button>
          </Paper>
        ) : (
          /* Report Form */
          <Paper
            p="xl"
            radius="lg"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #e9ecef',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {error && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    variant="light"
                    radius="md"
                  >
                    {error}
                  </Alert>
                )}

                <Select
                  label="Issue Type"
                  placeholder="Select issue type"
                  size="md"
                  radius="md"
                  required
                  data={ISSUE_TYPES}
                  value={issueType}
                  onChange={setIssueType}
                  classNames={{ input: 'form-input' }}
                />

                <TextInput
                  label="LEGO Set Number (optional)"
                  description="If this is about a specific set"
                  placeholder="e.g. 75192, 10294, 42151"
                  size="md"
                  radius="md"
                  value={setNumber}
                  onChange={(e) => setSetNumber(e.currentTarget.value)}
                  classNames={{ input: 'form-input' }}
                />

                <Textarea
                  label="Description"
                  placeholder="Please describe the issue in detail. For part issues, include the part color and which bag it should be in."
                  size="md"
                  radius="md"
                  required
                  minRows={4}
                  value={description}
                  onChange={(e) => setDescription(e.currentTarget.value)}
                  classNames={{ input: 'form-input' }}
                />

                <TextInput
                  label="Email (optional)"
                  description="Get notified when your issue is resolved"
                  placeholder="your@email.com"
                  size="md"
                  radius="md"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  classNames={{ input: 'form-input' }}
                />

                <Button
                  type="submit"
                  size="lg"
                  radius="xl"
                  loading={loading}
                  color="red"
                  leftSection={<IconSend size={18} />}
                  style={{
                    marginTop: 8,
                    boxShadow: '0 4px 12px rgba(250, 82, 82, 0.3)',
                  }}
                >
                  Submit Report
                </Button>
              </Stack>
            </form>
          </Paper>
        )}

        {/* Info Section */}
        <Paper
          p="lg"
          radius="lg"
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
          }}
        >
          <Title order={4} mb="sm" style={{ color: '#495057' }}>
            What can you report?
          </Title>
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">Part issues:</Text> Wrong parts, missing parts, or incorrect quantities
            </Text>
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">Bag issues:</Text> Parts assigned to the wrong bag number
            </Text>
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">Website bugs:</Text> Errors, display issues, or broken features
            </Text>
            <Text size="sm" c="dimmed">
              <Text component="span" fw={600} c="dark">Suggestions:</Text> Ideas to improve the website
            </Text>
          </Stack>
        </Paper>
      </Stack>
      </Container>
    </>
  );
}
